import { i18n } from '@/i18n'

function isVisibleField(el) {
  if (!(el instanceof Element)) {
    return false
  }
  if (el.classList.contains('custom-select-hidden')) {
    return false
  }
  const style = window.getComputedStyle(el)
  if (style.display === 'none' || style.visibility === 'hidden') {
    return false
  }
  const rect = el.getBoundingClientRect()
  return rect.width > 0 || rect.height > 0
}

function resolveScrollTarget(el) {
  return (
    el.closest('.mb-3')
    || el.closest('.form-field')
    || el.closest('[class*="col-"]')
    || el.closest('fieldset')
    || el
  )
}

function expandCollapsedAncestors(el) {
  let node = el instanceof Element ? el.parentElement : null
  while (node) {
    if (node.classList?.contains('card') && node.classList.contains('is-collapsed')) {
      node.classList.remove('is-collapsed')
      const btn = node.querySelector(':scope > .card-header > .card-collapse-btn')
      if (btn) {
        btn.setAttribute('aria-expanded', 'true')
        btn.setAttribute('aria-label', i18n.global.t('common.collapse'))
      }
    }
    node = node.parentElement
  }
}

function findFieldInContainer(container) {
  if (!(container instanceof Element)) {
    return null
  }
  return container.querySelector(
    'input:not([type="hidden"]), select:not(.custom-select-hidden), textarea, .choices, .form-select, .custom-select-trigger, [data-ltms-field]'
  )
}

function findTargetForErrorKey(scope, key) {
  if (!scope?.querySelector || !key) {
    return null
  }

  const escaped = CSS.escape(key)

  const byField = scope.querySelector(`[data-ltms-field="${escaped}"]`)
  if (byField) {
    return byField
  }

  const byDataField = scope.querySelector(`[data-field="${escaped}"]`)
  if (byDataField) {
    return findFieldInContainer(byDataField) || byDataField
  }

  const byError = scope.querySelector(`[data-ltms-error-for="${escaped}"]`)
  if (byError) {
    const container = byError.closest('.mb-3, .form-field, .col-sm-12, .col-12, [class*="col-"]')
    return findFieldInContainer(container) || container || byError
  }

  const byName = scope.querySelector(`[name="${escaped}"]`)
  if (byName) {
    return byName
  }

  return null
}

function expandCollapsedCardsWithErrors(scope) {
  if (!scope?.querySelectorAll) {
    return
  }

  scope.querySelectorAll('.card.is-collapsed').forEach(card => {
    if (card.querySelector('.is-invalid, [data-ltms-invalid], [data-ltms-error-for]')) {
      expandCollapsedAncestors(card)
    }
  })
}

function collectMarkerTargets(scope) {
  const selector = [
    '.is-invalid',
    '[data-ltms-invalid]',
    '.custom-select-trigger.is-invalid',
  ].join(', ')

  const targets = []
  for (const el of scope.querySelectorAll(selector)) {
    if (!isVisibleField(el) && !el.closest('.is-collapsed')) {
      continue
    }
    targets.push(resolveScrollTarget(el))
  }
  return targets
}

function sortTargetsByViewportTop(targets) {
  return [...new Set(targets)].sort(
    (a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top
  )
}

function focusField(target) {
  const focusable = target.querySelector(
    'input:not([type="hidden"]):not([readonly]), select:not(.custom-select-hidden):not([disabled]), textarea:not([readonly]), .choices__inner, .custom-select-trigger'
  )
  if (focusable instanceof HTMLElement) {
    focusable.focus({ preventScroll: true })
  }
}

/**
 * Прокручивает страницу к первому видимому невалидному полю формы.
 * Ищет `.is-invalid`, `[data-ltms-invalid]`, `[data-ltms-field]`, `[data-ltms-error-for]`.
 *
 * @param {Document|Element|null|undefined} root
 * @param {{ offsetTop?: number, errorKeys?: string[] }} [options]
 * @returns {boolean}
 */
export function scrollToFirstInvalidField(root, { offsetTop = 88, errorKeys = [] } = {}) {
  const scope = root instanceof Element ? root : document
  expandCollapsedCardsWithErrors(scope)

  const targets = []

  for (const key of errorKeys) {
    const el = findTargetForErrorKey(scope, key)
    if (el) {
      targets.push(resolveScrollTarget(el))
    }
  }

  targets.push(...collectMarkerTargets(scope))

  const candidates = sortTargetsByViewportTop(targets)
  if (!candidates.length) {
    return false
  }

  const target = candidates[0]
  expandCollapsedAncestors(target)

  const top = window.scrollY + target.getBoundingClientRect().top - offsetTop
  window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' })
  focusField(target)
  return true
}

export function scrollTo(el, offset, callback) {
  const fixedOffset = offset.toFixed()
  const onScroll = () => {
    if (el.scrollTop.toFixed() === fixedOffset) {
      el.removeEventListener('scroll', onScroll)
      callback()
    }
  }
  el.addEventListener('scroll', onScroll)
  onScroll()
  el.scrollTo({
    top: offset,
    behavior: 'smooth',
  })
}
